import type { ReactNode } from 'react'
import {
  IconeCamadas,
  IconeCarro,
  IconeFerramenta,
  IconePausar,
  IconeReiniciar,
  IconeReproduzir,
} from '../icones'
import type { ConfiguracaoOficinaCorte, FerramentaOficinaCorte, ModoOficinaCorte } from '../PainelOficinaCorte'
import type { PlayerOficinaCorte } from '../oficina/usePlayerOficinaCorte'
import type { ModoAlargador } from '../../dominio/didatico/interacaoOficina'
import {
  ATALHO_MODO_ALARGADOR,
  FERRAMENTAS_DE_INTERVENCAO,
  MODOS_DO_ALARGADOR,
  dicaDaFerramenta,
} from '../oficina/ferramentasOficina'
import type { Ferramenta as FerramentaDaTrilha } from '../workspace/TrilhaFerramentas'
import type { FerramentasModulo } from './tipos'

interface Props {
  painelCenario: () => ReactNode
  painelFerramentas?: () => ReactNode
  painelVidros?: () => ReactNode
  painelReforcos: () => ReactNode
  painelTecnica: () => ReactNode
  configuracao: ConfiguracaoOficinaCorte
  player: PlayerOficinaCorte
  modo?: ModoOficinaCorte
  aoMudarModo?: (modo: ModoOficinaCorte) => void
  ferramenta?: FerramentaOficinaCorte
  modoAlargador?: ModoAlargador
  motivoDaFerramenta?: (ferramenta: FerramentaOficinaCorte) => string | undefined
  acoesDePreparo?: readonly FerramentaDaTrilha[]
  incluirPreparo?: boolean
  aoEscolherFerramenta?: (ferramenta: FerramentaOficinaCorte) => void
  aoEscolherModoAlargador?: (modo: ModoAlargador) => void
  vinculosRestantes?: number
  aoReiniciarPratica?: () => void
}

/**
 * Ações rápidas e grupos da Oficina de corte (Demonstração guiada e Prática livre).
 */
export function ferramentasCorte(p: Props): FerramentasModulo {
  const { player, modo = 'demonstracao' } = p

  if (modo === 'pratica') {
    const ferramentaAtual = FERRAMENTAS_DE_INTERVENCAO.find((f) => f.id === p.ferramenta)
    return {
      grupos: [
        { id: 'tecnica', rotulo: 'Prática de Corte', icone: <IconeFerramenta />, conteudo: p.painelTecnica },
        { id: 'cenario', rotulo: 'Tipo de veículo', icone: <IconeCarro />, conteudo: p.painelCenario },
        { id: 'reforcos', rotulo: 'Reforços', icone: <IconeCamadas />, conteudo: p.painelReforcos },
      ],
      chips: [
        { id: 'modo', rotulo: 'Modo', valor: 'Prática livre', tom: 'sucesso' },
        { id: 'ferramenta', rotulo: 'Equipamento', valor: ferramentaAtual?.nomeCurto ?? p.ferramenta ?? 'Cortador', tom: 'info' },
        { id: 'vinculos', rotulo: 'Vínculos', valor: `${p.vinculosRestantes ?? 0} restantes`, tom: (p.vinculosRestantes ?? 0) === 0 ? 'sucesso' : 'alerta' },
      ],
      acoesRapidas: (
        <div className="acoes-rapidas-oficina" role="group" aria-label="Controles de Prática de Oficina">
          <button
            type="button"
            className="btn-acao-cad btn-ferramenta-oficina btn-acao-cad--secundario"
            onClick={() => p.aoMudarModo?.('demonstracao')}
            title="Alternar para demonstração guiada da técnica"
          >
            <IconeReproduzir tamanho={14} /> Modo guiado
          </button>

          {FERRAMENTAS_DE_INTERVENCAO.map((ferramenta) => {
            const motivo = p.motivoDaFerramenta?.(ferramenta.id)
            const equipada = p.ferramenta === ferramenta.id
            return (
              <button
                key={ferramenta.id}
                type="button"
                className={`btn-acao-cad btn-ferramenta-oficina ${equipada ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
                aria-pressed={equipada}
                disabled={motivo != null}
                title={dicaDaFerramenta(ferramenta, motivo)}
                onClick={() => p.aoEscolherFerramenta?.(ferramenta.id)}
              >
                <ferramenta.Icone tamanho={15} />
                {ferramenta.nomeCurto}
              </button>
            )
          })}

          {p.ferramenta === 'alargador' && MODOS_DO_ALARGADOR.map((modoItem) => (
            <button
              key={modoItem.id}
              type="button"
              className={`btn-acao-cad btn-ferramenta-oficina ${p.modoAlargador === modoItem.id ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
              aria-pressed={p.modoAlargador === modoItem.id}
              title={`${modoItem.nome} (${ATALHO_MODO_ALARGADOR}) — ${modoItem.dica}`}
              onClick={() => p.aoEscolherModoAlargador?.(modoItem.id)}
            >
              <modoItem.Icone tamanho={15} />
              {modoItem.nomeCurto}
            </button>
          ))}

          {(p.incluirPreparo ? (p.acoesDePreparo ?? []) : []).map((acao) => (
            <button
              key={acao.id}
              type="button"
              className={`btn-acao-cad btn-ferramenta-oficina ${acao.ativa ? 'btn-acao-cad--primario' : 'btn-acao-cad--secundario'}`}
              aria-pressed={acao.ativa}
              disabled={acao.motivoIndisponivel != null}
              title={acao.motivoIndisponivel ? `${acao.rotulo} — ${acao.motivoIndisponivel}` : acao.rotulo}
              onClick={acao.aoAcionar}
            >
              <acao.Icone tamanho={15} />
              {acao.rotulo}
            </button>
          ))}

          {p.aoReiniciarPratica && (
            <button
              type="button"
              className="btn-acao-cad btn-ferramenta-oficina btn-acao-cad--secundario"
              onClick={p.aoReiniciarPratica}
              title="Reiniciar prática e restaurar peça"
            >
              <IconeReiniciar tamanho={15} />
            </button>
          )}
        </div>
      ),
    }
  }

  return {
    grupos: [
      { id: 'tecnica', rotulo: 'Técnica', icone: <IconeFerramenta />, conteudo: p.painelTecnica },
      { id: 'cenario', rotulo: 'Tipo de veículo', icone: <IconeCarro />, conteudo: p.painelCenario },
      { id: 'reforcos', rotulo: 'Reforços', icone: <IconeCamadas />, conteudo: p.painelReforcos },
    ],
    chips: [
      { id: 'modo', rotulo: 'Modo', valor: 'Demonstração', tom: 'info' },
      { id: 'etapa', rotulo: 'Etapa', valor: `${player.indiceEtapa + 1}/${player.tecnica.etapas.length}` },
    ],
    acoesRapidas: (
      <div className="acoes-rapidas-oficina" role="group" aria-label="Controles do Player Didático">
        {/* Sem atalho para a prática livre: por ora a oficina fica só no modo
            guiado (o posicionamento livre das ferramentas ainda não convence).
            O caminho `modo === 'pratica'` acima continua de pé para quando voltar. */}
        <button
          type="button"
          className={`btn-acao-cad btn-ferramenta-oficina ${player.reproduzindo ? 'btn-acao-cad--secundario' : 'btn-acao-cad--primario'}`}
          onClick={player.alternarReproducao}
          title={player.reproduzindo ? 'Pausar demonstração' : 'Reproduzir etapa'}
        >
          {player.reproduzindo ? (
            <>
              <IconePausar tamanho={15} /> Pausar
            </>
          ) : (
            <><IconeReproduzir tamanho={15} /> {player.estadoAnimacao === 'concluido' ? 'Assistir novamente' : 'Continuar'}</>
          )}
        </button>

        <button
          type="button"
          className="btn-acao-cad btn-ferramenta-oficina btn-acao-cad--secundario"
          onClick={player.reiniciar}
          title="Reiniciar técnica"
        >
          <IconeReiniciar tamanho={15} />
        </button>
      </div>
    ),
  }
}
